import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Star, Clock, CheckCircle, ShieldCheck, CalendarCheck, AlertTriangle, Check, X, MapPin, Bell, Users, CalendarClock, MessageSquare, Shield } from 'lucide-react';
import { formatAddress } from '../../utils/format';
import { toast } from 'sonner';
import { RescheduleDialog } from '../client/RescheduleDialog';
import SafetyBookingDialog from '../safety/SafetyBookingDialog';
import ActiveShareLinksSection from '../safety/ActiveShareLinksSection';
import { EmptyState, copyStorefrontLink } from './EmptyState';

const LIMIT = 10;

function WaitlistSection() {
  const [waitlist, setWaitlist] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tick once a second so the offer countdown stays live without
  // re-fetching the whole list (the backend stamps an absolute
  // expires_at, the client just renders the delta).
  const [_tick, setTick] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/waitlist/provider');
        setWaitlist(res.data);
      } catch { setWaitlist({ slots: [], is_pro: false }); }
      finally { setLoading(false); }
    };
    fetch();
    // Refresh every 30s to catch new offers + auto-rollovers
    const refresh = setInterval(fetch, 30000);
    const ticker = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(refresh); clearInterval(ticker); };
  }, []);

  const fmtCountdown = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expiring…';
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) return null;
  // Smart Waitlists is a Pro-tier perk, hide entirely on Free tier
  if (!waitlist?.is_pro) return null;

  return (
    <div className="card-surface p-5 mb-6" data-testid="provider-waitlist-section">
      <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
        <Bell className="h-4 w-4 text-[#D4AF37]" />
        Waitlist
        {waitlist && waitlist.entries.length > 0 && (
          <span className="text-xs text-stone-400 font-normal">({waitlist.entries.length} {waitlist.entries.length === 1 ? 'person' : 'people'} waiting)</span>
        )}
      </h3>
      {waitlist && waitlist.slots.length > 0 ? (
        <div className="space-y-3">
          {waitlist.slots.map((slot) => (
            <div
              key={`${slot.service_id || slot.service_name}-${slot.date}-${slot.time}`}
              className="p-3 bg-[#F9F6F0] rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-[#1A1A1A]">{slot.service_name}</p>
                <p className="text-xs text-stone-400">{slot.date} at {slot.time}</p>
              </div>

              {/* Live priority offer banner */}
              {slot.active_offer && (
                <div
                  className="mb-2 px-3 py-2 bg-white border-l-4 border-[#D4AF37] rounded-r-lg flex items-center justify-between text-xs"
                  data-testid={`waitlist-active-offer-${slot.date}-${slot.time}`}
                >
                  <span className="text-[#1A1A1A]">
                    <strong>Priority offer</strong> sent to{' '}
                    <span className="font-medium">{slot.active_offer.client_name}</span>
                  </span>
                  <span className="font-mono text-[#D4AF37] font-semibold">
                    {fmtCountdown(slot.active_offer.offer_expires_at)} left
                  </span>
                </div>
              )}

              <div className="flex items-center gap-1.5 flex-wrap">
                <Users className="h-3.5 w-3.5 text-stone-400" />
                {slot.entries.map((entry) => {
                  const isOffered = entry.status === 'offered';
                  const isClaimed = entry.status === 'claimed';
                  return (
                    <span
                      key={entry.entry_id || `${entry.client_id}-${entry.client_name}`}
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        isOffered
                          ? 'bg-[#D4AF37]/15 border border-[#D4AF37] text-[#1A1A1A] font-medium'
                          : isClaimed
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-900 font-medium'
                          : 'bg-white border border-[#E6E4DD] text-stone-600'
                      }`}
                    >
                      {entry.client_name}
                      {isOffered && ' • offered'}
                      {isClaimed && ' • claimed'}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-stone-400">No one on the waitlist yet. When a slot is fully booked, clients can join. If a booking is cancelled, we offer it to the first person in line with a 15-minute window, then auto-roll to the next, no chasing needed.</p>
      )}
    </div>
  );
}

const STATUS_CONFIG = {
  confirmed: { style: 'bg-green-100 text-green-700', label: 'Confirmed' },
  pending: { style: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
  completed: { style: 'bg-blue-100 text-blue-700', label: 'Completed' },
  cancelled: { style: 'bg-red-100 text-red-700', label: 'Cancelled' },
  disputed: { style: 'bg-red-100 text-red-700', label: 'Disputed' },
  reschedule_requested: { style: 'bg-purple-100 text-purple-700', label: 'Reschedule' },
};

// Bookings tab shows ONLY booking lifecycle statuses, payment lifecycle lives
// in Earnings → Transactions. Payment-state rows (pending_payment,
// failed_payment, retrying) are filtered out of the provider bookings view
// below; the UI surfaces a simple "Paid/Unpaid" badge instead.
const BOOKING_LIFECYCLE_STATUSES = new Set([
  'pending', 'confirmed', 'completed', 'cancelled', 'disputed', 'reschedule_requested',
]);

const AUTO_COMPLETE_GRACE_HOURS = 2;

function getAutoCompleteState(booking) {
  // Only relevant for confirmed bookings.
  if (booking.status !== 'confirmed') return null;
  const { date, time, duration } = booking;
  if (!date || !time || !duration) return null;
  const start = new Date(`${date}T${time}:00Z`);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + duration * 60 * 1000);
  const autoAt = new Date(end.getTime() + AUTO_COMPLETE_GRACE_HOURS * 60 * 60 * 1000);
  const now = Date.now();
  if (now < end.getTime()) return null; // appointment hasn't ended yet
  const msLeft = autoAt.getTime() - now;
  if (msLeft <= 0) return { pending: false, label: 'Auto-completing...' };
  const minutesLeft = Math.ceil(msLeft / 60000);
  const label = minutesLeft >= 60
    ? `Auto-completes in ${Math.ceil(minutesLeft / 60)}h`
    : `Auto-completes in ${minutesLeft}m`;
  return { pending: true, label };
}

function ClientTrustBadge({ trust }) {
  if (!trust) return null;
  const { completed_bookings, cancellation_rate, is_verified, is_guest } = trust;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1.5 mt-0.5">
        {is_verified && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full" data-testid="client-verified-badge">
                <ShieldCheck className="h-3 w-3" />Verified
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">Email verified account</p></TooltipContent>
          </Tooltip>
        )}
        {is_guest && (
          <span className="inline-flex items-center text-[10px] font-medium text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded-full" data-testid="client-guest-badge">
            Guest
          </span>
        )}
        {completed_bookings > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full" data-testid="client-bookings-badge">
                <CalendarCheck className="h-3 w-3" />{completed_bookings}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">{completed_bookings} completed booking{completed_bookings !== 1 ? 's' : ''}</p></TooltipContent>
          </Tooltip>
        )}
        {cancellation_rate > 20 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full" data-testid="client-cancel-badge">
                <AlertTriangle className="h-3 w-3" />{cancellation_rate}%
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">{cancellation_rate}% cancellation rate</p></TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

export function BookingsTab({ bookings, handleBookingStatus, username }) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const [reviewEligibility, setReviewEligibility] = useState({});
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  // Provider-initiated reschedule flow (separate from reschedule-request approval).
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [bookingToReschedule, setBookingToReschedule] = useState(null);
  // Per-booking Safety dialog (check-ins, panic, share link).
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [safetyBooking, setSafetyBooking] = useState(null);

  // Only surface bookings whose STATUS is a booking-lifecycle state. Payment-
  // only rows (pending_payment / failed_payment / retrying) belong on the
  // Earnings → Transactions view, not here.
  const lifecycleBookings = bookings.filter(
    (b) => BOOKING_LIFECYCLE_STATUSES.has(b.status),
  );
  const displayed = showAll ? lifecycleBookings : lifecycleBookings.slice(0, LIMIT);
  const hasMore = bookings.length > LIMIT;

  const checkEligibility = useCallback(async (bookingId) => {
    try {
      const res = await api.get(`/reviews/check/${bookingId}`);
      setReviewEligibility(prev => ({ ...prev, [bookingId]: res.data }));
    } catch (err) {
      console.error('Review eligibility check failed:', err);
    }
  }, []);

  useEffect(() => {
    const completed = bookings.filter(b => b.status === 'completed');
    completed.forEach(b => {
      if (!reviewEligibility[b.booking_id]) checkEligibility(b.booking_id);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const handleReviewClient = async () => {
    if (!selectedBooking) return;
    try {
      await api.post('/reviews', {
        booking_id: selectedBooking.booking_id,
        rating: reviewRating,
        text: reviewText,
        review_type: 'provider_to_client',
      });
      toast.success('Client review submitted!');
      setReviewDialogOpen(false);
      setSelectedBooking(null);
      setReviewRating(5);
      setReviewText('');
      setReviewEligibility(prev => ({
        ...prev,
        [selectedBooking.booking_id]: { ...prev[selectedBooking.booking_id], can_review: false, provider_review_submitted: true },
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
  };

  const handleRescheduleResponse = async (bookingId, action) => {
    try {
      await api.post(`/bookings/${bookingId}/reschedule/respond`, { action });
      toast.success(action === 'approve' ? 'Reschedule approved' : 'Reschedule declined');
      // Trigger parent refresh
      if (typeof handleBookingStatus === 'function') handleBookingStatus(bookingId, 'refresh');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    }
  };

  const handleProviderCancel = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/cancel`, { reason: 'Cancelled by provider' });
      toast.success('Booking cancelled');
      if (typeof handleBookingStatus === 'function') handleBookingStatus(bookingId, 'refresh');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <div className="card-surface p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">All Bookings</h3>
        {hasMore && (
          <span className="text-sm text-stone-400">{bookings.length} total</span>
        )}
      </div>
      {bookings.length === 0 ? (
        <EmptyState
          icon={CalendarCheck}
          testId="bookings-empty-state"
          title="No bookings yet"
          body={username
            ? 'Share your booking link where your clients already are. Bookings made through your own link carry no commission.'
            : 'Once clients book you, every appointment shows up here with its status and payment.'}
          actionLabel={username ? 'Copy booking link' : null}
          onAction={async () => {
            const ok = await copyStorefrontLink(username);
            if (ok) toast.success('Booking link copied'); else toast.error('Could not copy the link');
          }}
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E6E4DD]">
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Ref</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Service</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Client</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Date & Time</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Location</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Payment</th>
                  <th className="text-left py-3 px-4 text-stone-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((booking) => {
                  const elig = reviewEligibility[booking.booking_id];
                  const isCompleted = booking.status === 'completed';
                  const providerReviewed = elig?.provider_review_submitted;
                  const canReview = elig?.can_review && elig?.is_provider;
                  const hoursLeft = elig?.hours_remaining;

                  return (
                    <tr key={booking.booking_id} className="border-b border-[#E6E4DD] hover:bg-[#F9F6F0]">
                      <td className="py-4 px-4 font-mono text-xs text-stone-400">{booking.booking_ref || booking.booking_id.slice(0, 12)}</td>
                      <td className="py-4 px-4 font-medium text-[#1A1A1A]">{booking.service_name}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-start gap-2">
                          <div className="min-w-0">
                            <span className="text-stone-600">{booking.client_name}</span>
                            <ClientTrustBadge trust={booking.client_trust} />
                          </div>
                          {!booking.is_guest && booking.client_id && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/messages?user=${booking.client_id}`)}
                                    className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-stone-500 hover:text-[#D4AF37] hover:bg-amber-50 transition-colors"
                                    data-testid={`message-client-${booking.booking_id}`}
                                    aria-label={`Message ${booking.client_name}`}
                                  >
                                    <MessageSquare className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top"><p className="text-xs">Message {booking.client_name}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-stone-600">{booking.date} at {booking.time}</td>
                      <td className="py-4 px-4">
                        <div className="text-xs text-stone-500">
                          <span className="capitalize">{booking.service_location || '-'}</span>
                          {booking.service_location === 'mobile' && booking.client_address && (
                            <div className="mt-1 flex items-start gap-1 text-[11px] text-stone-600 bg-amber-50 border border-amber-100 rounded px-2 py-1" data-testid={`client-address-${booking.booking_id}`}>
                              <MapPin className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                              <span>{formatAddress(booking.client_address)}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[booking.status]?.style || 'bg-stone-100 text-stone-600'}`}>
                          {STATUS_CONFIG[booking.status]?.label || booking.status}
                        </span>
                        {booking.status === 'reschedule_requested' && booking.reschedule_request && (
                          <p className="text-[10px] text-purple-500 mt-0.5">{booking.reschedule_request.new_date} {booking.reschedule_request.new_time}</p>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
                        }`} data-testid={`payment-badge-${booking.booking_id}`}>
                          {booking.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2 items-center flex-wrap">
                          {booking.status === 'pending' && (
                            <Button size="sm" className="btn-primary" onClick={() => handleBookingStatus(booking.booking_id, 'confirmed')}>Confirm</Button>
                          )}
                          {booking.status === 'confirmed' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleBookingStatus(booking.booking_id, 'completed')}>Mark Complete</Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-[#D4AF37] border-[#D4AF37]/40 hover:bg-amber-50 gap-1"
                                onClick={() => { setBookingToReschedule(booking); setRescheduleOpen(true); }}
                                data-testid={`provider-reschedule-${booking.booking_id}`}
                                title="Reschedule this booking"
                              >
                                <CalendarClock className="h-3.5 w-3.5" />
                                Reschedule
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleProviderCancel(booking.booking_id)} data-testid={`provider-cancel-${booking.booking_id}`}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                              {booking.service_location === 'mobile' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[#D4AF37] border-[#D4AF37]/40 hover:bg-amber-50 gap-1"
                                  onClick={() => { setSafetyBooking(booking); setSafetyOpen(true); }}
                                  data-testid={`provider-safety-${booking.booking_id}`}
                                  title="Safety check-ins, share link, panic alert"
                                >
                                  <Shield className="h-3.5 w-3.5" />
                                  Safety
                                </Button>
                              )}
                              {(() => {
                                const auto = getAutoCompleteState(booking);
                                if (!auto) return null;
                                return (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"
                                          data-testid={`auto-complete-notice-${booking.booking_id}`}
                                        >
                                          <Clock className="h-3 w-3" />
                                          {auto.label}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="max-w-[240px]">
                                        <p className="text-xs">
                                          This booking will be marked completed automatically in {AUTO_COMPLETE_GRACE_HOURS} hours unless updated.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              })()}
                            </>
                          )}
                          {booking.status === 'reschedule_requested' && (
                            <>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" onClick={() => handleRescheduleResponse(booking.booking_id, 'approve')} data-testid={`approve-reschedule-${booking.booking_id}`}>
                                <Check className="h-3.5 w-3.5" />Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 gap-1" onClick={() => handleRescheduleResponse(booking.booking_id, 'reject')} data-testid={`reject-reschedule-${booking.booking_id}`}>
                                <X className="h-3.5 w-3.5" />Decline
                              </Button>
                            </>
                          )}
                          {isCompleted && providerReviewed && (
                            <span className="text-xs text-green-600 flex items-center gap-1" data-testid={`provider-reviewed-${booking.booking_id}`}>
                              <CheckCircle className="h-3.5 w-3.5" />Reviewed
                            </span>
                          )}
                          {isCompleted && canReview && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50 gap-1"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setReviewRating(5);
                                setReviewText('');
                                setReviewDialogOpen(true);
                              }}
                              data-testid={`review-client-${booking.booking_id}`}
                            >
                              <Star className="h-3.5 w-3.5" />Review Client
                            </Button>
                          )}
                          {isCompleted && !providerReviewed && !canReview && hoursLeft && (
                            <span className="text-xs text-stone-400 flex items-center gap-1" data-testid={`review-countdown-${booking.booking_id}`}>
                              <Clock className="h-3.5 w-3.5" />{Math.ceil(hoursLeft)}h until review
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-4 text-center">
              <Button
                variant="outline"
                onClick={() => setShowAll(!showAll)}
                className="border-[#E6E4DD] text-stone-600 hover:text-[#1A1A1A]"
                data-testid="bookings-view-all-btn"
              >
                {showAll ? 'Show Less' : `View All ${bookings.length} Bookings`}
              </Button>
            </div>
          )}
        </>
      )}


      {/* Waitlist Section */}
      <WaitlistSection />

      {/* Active safety share links, only renders if any are live */}
      <ActiveShareLinksSection />

      {/* Review Client Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Review {selectedBooking?.client_name || 'Client'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-2 block">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button key={star} onClick={() => setReviewRating(star)} className="p-1" data-testid={`provider-star-${star}`}>
                    <Star className={`h-8 w-8 ${star <= reviewRating ? 'text-[#D4AF37] fill-current' : 'text-stone-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-2 block">Your Review</label>
              <Textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience with this client..."
                className="input-zuriya min-h-[100px]"
                data-testid="provider-review-text-input"
              />
            </div>
            <Button onClick={handleReviewClient} className="w-full btn-primary" data-testid="submit-provider-review-btn">
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Provider-initiated reschedule dialog, reuses the client-facing
          picker which is already driven by the provider's live calendar. */}
      <RescheduleDialog
        open={rescheduleOpen}
        onOpenChange={(v) => { setRescheduleOpen(v); if (!v) setBookingToReschedule(null); }}
        booking={bookingToReschedule}
        onRescheduled={() => {
          const id = bookingToReschedule?.booking_id;
          setRescheduleOpen(false);
          setBookingToReschedule(null);
          if (id && typeof handleBookingStatus === 'function') handleBookingStatus(id, 'refresh');
        }}
      />

      <SafetyBookingDialog
        booking={safetyBooking}
        open={safetyOpen}
        onOpenChange={(v) => { setSafetyOpen(v); if (!v) setSafetyBooking(null); }}
      />
    </div>
  );
}
